const DIRECTIONS = new Set(["OVER", "UNDER"]);

const clean = (value) => String(value ?? "").trim().toUpperCase().replace(/[_\s]+/g, " ");

const exactDirection = (value) => {
  const token = clean(value);
  if (token === "OVER" || token.startsWith("OVER (")) return "OVER";
  if (token === "UNDER" || token.startsWith("UNDER (")) return "UNDER";
  return "";
};

export const recommendationDirection = (recommendation) => {
  const token = clean(recommendation);
  if (["MEGA SMASH", "SMASH", "LEAN OVER"].includes(token)) return "OVER";
  if (["MEGA FADE", "FADE", "LEAN UNDER"].includes(token)) return "UNDER";
  return "";
};

export const governedDirection = (row = {}) => {
  const nhlSignal = clean(row.DirectionBucket);
  if (nhlSignal === "UP") return "OVER";
  if (nhlSignal === "DOWN") return "UNDER";
  if (nhlSignal === "WAIT") return "NEUTRAL";
  const explicit = [
    row.Direction,
    row.direction,
    row["Guru Direction"],
    row.Guru_Pick_Direction,
    row.guru_pick_direction,
    row.SS_Direction,
    row["SS_Direction"],
    row.Guru_Pick_Base,
    row.Guru_Final,
    row.Guru_Pick,
    row["Guru Pick"],
    row.SelectionStrategy,
  ].map(exactDirection).find(Boolean);
  if (explicit) return explicit;

  const recommendation = clean(row.Tag_Final ?? row.Final_Tag ?? row.Tag ?? row.Recommendation);
  if (recommendation.includes("COIN")) return "NEUTRAL";
  return recommendationDirection(recommendation) || "NEUTRAL";
};

const pct = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return number <= 1 ? number * 100 : number;
};

const displayPercent = (value) => `${Number(value).toFixed(1)}%`;

const confidence = (row = {}) => {
  const raw = row.Guru_Pick_Confidence ?? row["Guru_Pick_Confidence"] ?? row.PWG_Prob_Tier ?? row["PWG_Prob_Tier"] ?? row.ConfidenceLabel ?? row.ConfidenceTier ?? row.Confidence;
  const token = clean(raw);
  if (["ELITE", "VOLATILE ELITE"].includes(token)) return { confidenceTier: token === "VOLATILE ELITE" ? "Volatile Elite" : "Elite", confidenceSemantic: "STRENGTH", confidenceIcon: "SIGNAL", confidenceLevel: 4 };
  if (["HIGH", "STRONG"].includes(token)) return { confidenceTier: "Strong", confidenceSemantic: "STRENGTH", confidenceIcon: "SIGNAL", confidenceLevel: 3 };
  if (["MED", "MEDIUM", "MODERATE"].includes(token)) return { confidenceTier: "Moderate", confidenceSemantic: "STRENGTH", confidenceIcon: "SIGNAL", confidenceLevel: 2 };
  if (token === "LOW") return { confidenceTier: "Low", confidenceSemantic: "STRENGTH", confidenceIcon: "SIGNAL", confidenceLevel: 1 };
  if (["EARLY", "EARLY+"].includes(token)) return { confidenceTier: token === "EARLY+" ? "Early+" : "Early", confidenceSemantic: "STATUS", confidenceIcon: "DEVELOPING", confidenceLevel: null };
  if (token.includes("AVOID") || token === "STAY AWAY") return { confidenceTier: token.includes("AVOID") ? "Avoid" : "Stay Away", confidenceSemantic: "STATUS", confidenceIcon: "AVOID", confidenceLevel: null };
  const numeric = Number(raw);
  if (Number.isFinite(numeric)) {
    if (numeric > 10) return { confidenceTier: "Volatile Elite", confidenceSemantic: "STRENGTH", confidenceIcon: "SIGNAL", confidenceLevel: 4 };
    if (numeric >= 7.5) return { confidenceTier: "Elite", confidenceSemantic: "STRENGTH", confidenceIcon: "SIGNAL", confidenceLevel: 4 };
    if (numeric >= 5) return { confidenceTier: "Strong", confidenceSemantic: "STRENGTH", confidenceIcon: "SIGNAL", confidenceLevel: 3 };
    if (numeric >= 2.5) return { confidenceTier: "Moderate", confidenceSemantic: "STRENGTH", confidenceIcon: "SIGNAL", confidenceLevel: 2 };
    if (numeric >= 0) return { confidenceTier: "Low", confidenceSemantic: "STRENGTH", confidenceIcon: "SIGNAL", confidenceLevel: 1 };
  }
  return { confidenceTier: token && !Number.isFinite(Number(raw)) ? String(raw).trim() : "", confidenceSemantic: "UNKNOWN", confidenceIcon: "NEUTRAL", confidenceLevel: null };
};

export const directionPresentation = (row = {}, sport = "") => {
  const sportKey = clean(sport || row.Sport || row.League);
  const direction = sportKey === "NHL" ? exactDirection(row.Direction) : governedDirection(row);
  const recommendation = String(row.DirectionBucket ?? row.Tag_Final ?? row.Final_Tag ?? row.Tag ?? row.Recommendation ?? "").trim();
  let numericValue = null;
  let numericLabel = "";

  if (sportKey === "NHL") {
    if (!DIRECTIONS.has(direction)) return { direction: "INVALID", recommendation, numericValue: null, numericLabel: "", confidenceTier: "", confidenceSemantic: "UNKNOWN", confidenceIcon: "NEUTRAL", confidenceLevel: null };
    const governedConfidence = pct(row.Confidence);
    if (governedConfidence !== null) { numericValue = displayPercent(governedConfidence); numericLabel = "Governed Confidence"; }
    const capped = governedConfidence === null ? {} : {
      confidenceTier: `${Number(governedConfidence).toFixed(0)}%`, confidenceSemantic: "CONFIDENCE", confidenceIcon: "NEUTRAL",
      confidenceLevel: null,
    };
    return { direction, recommendation, numericValue, numericLabel, ...confidence(row), ...capped };
  }
  if (sportKey === "MLB" && DIRECTIONS.has(direction)) {
    const value = direction === "OVER" ? pct(row.PWG_Prob_Over ?? row["PWG_Prob_Over"]) : pct(row.PWG_Prob_Under ?? row["PWG_Prob_Under"]);
    if (value !== null) { numericValue = displayPercent(value); numericLabel = "PWG Estimate"; }
  } else {
    const guru = pct(row["Guru Confidence"] ?? row.Guru_Confidence);
    if (guru !== null) { numericValue = displayPercent(guru); numericLabel = "Guru Confidence"; }
  }

  return { direction, recommendation, numericValue, numericLabel, ...confidence(row) };
};

export const directionRailColor = (directionMeta = {}, sport = "") => {
  const direction = exactDirection(directionMeta?.direction);
  if (direction === "OVER") return "#15803d";
  if (direction === "UNDER") return "#b91c1c";
  return clean(sport) === "NHL" ? null : "#64748b";
};

export const directionFilterMatches = (row, selected, sport) => {
  const wanted = clean(selected || "ALL");
  if (wanted === "ALL") return true;
  const direction = directionPresentation(row, sport).direction;
  return wanted === direction;
};
